'use client'

import { Card } from '@acko/card'
import { Accordion } from '@acko/accordion'
import { Typography } from '@acko/typography'
import { Dropdown } from '@acko/dropdown'
import { ChevronDown, Close } from '@acko/icons'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styles from './page.module.css'

/* ─── Pricing Data & Logic (from nextjs-handoff/lib/pricing.ts) ──────────── */

const MAKES = ['Royal Enfield', 'Bajaj', 'Honda', 'Yamaha', 'KTM', 'TVS', 'Hero', 'Suzuki'] as const
const MODELS_BY_MAKE: Record<string, string[]> = {
  'Royal Enfield': ['Classic 350', 'Hunter 350', 'Meteor 350', 'Himalayan 450', 'Interceptor 650'],
  Bajaj: ['Pulsar NS200', 'Pulsar 150', 'Dominar 400', 'Avenger 220'],
  Honda: ['CB350', 'Shine 125', 'Unicorn', 'CB300R', 'Hornet 2.0'],
  Yamaha: ['MT-15', 'R15 V4', 'FZ-S', 'Aerox 155'],
  KTM: ['Duke 200', 'Duke 390', 'RC 390', 'Adventure 390'],
  TVS: ['Apache RTR 160', 'Apache RR 310', 'Ronin', 'Raider'],
  Hero: ['Splendor Plus', 'Xtreme 160R', 'Xpulse 200', 'Passion Pro'],
  Suzuki: ['Gixxer 250', 'Access 125', 'Burgman Street', 'V-Strom SX'],
}
const VARIANTS = ['Standard', 'Dual Channel ABS', 'Dark Edition', 'Pro'] as const
const YEARS = ['2026', '2025', '2024', '2023', '2022', '2021', '2020', '2019', '2018'] as const
const ADDONS = ['Engine protect', 'Consumables', 'Accessories', 'Personal accident', 'Extra protection'] as const
const NCB_OPTIONS = ['0%', '10%', '20%', '40%'] as const
const PLAN_OPTIONS = ['Comprehensive', 'Own damage', 'Third-party'] as const

const ONROAD_BY_MODEL: Record<string, number> = {
  'Classic 350': 215000,
  'Hunter 350': 175000,
  'Meteor 350': 230000,
  'Himalayan 450': 315000,
  'Interceptor 650': 345000,
  'Pulsar NS200': 175000,
  'Pulsar 150': 125000,
  'Dominar 400': 245000,
  'Avenger 220': 155000,
  CB350: 235000,
  'Shine 125': 95000,
  Unicorn: 125000,
  CB300R: 270000,
  'Hornet 2.0': 158000,
  'MT-15': 185000,
  'R15 V4': 215000,
  'FZ-S': 145000,
  'Aerox 155': 175000,
  'Duke 200': 220000,
  'Duke 390': 335000,
  'RC 390': 355000,
  'Adventure 390': 380000,
  'Apache RTR 160': 145000,
  'Apache RR 310': 315000,
  Ronin: 190000,
  Raider: 115000,
  'Splendor Plus': 85000,
  'Xtreme 160R': 140000,
  'Xpulse 200': 165000,
  'Passion Pro': 90000,
  'Gixxer 250': 220000,
  'Access 125': 105000,
  'Burgman Street': 120000,
  'V-Strom SX': 240000,
}
const VARIANT_MULT: Record<string, number> = {
  Standard: 1.0,
  'Dual Channel ABS': 1.06,
  'Dark Edition': 1.08,
  Pro: 1.12,
}
const ADDON_COST: Record<string, number> = {
  'Engine protect': 1200,
  Consumables: 600,
  Accessories: 800,
  'Personal accident': 450,
  'Extra protection': 900,
}
const NCB_MULT: Record<string, number> = { '0%': 1, '10%': 0.9, '20%': 0.8, '40%': 0.6 }
const PLAN_MULT: Record<string, number> = { Comprehensive: 1, 'Own damage': 0.75, 'Third-party': 0.4 }

type CalcState = { make: string; model: string; variant: string; year: string; addons: string[]; ncb: string }

function getStatus(s: CalcState): 'empty' | 'partial' | 'complete' {
  if (!(s.make && s.model && s.variant && s.year)) return 'empty'
  if (!s.ncb) return 'partial'
  return 'complete'
}

function depreciationForAge(age: number) {
  if (age <= 0.5) return 0.05
  if (age <= 1) return 0.15
  if (age <= 2) return 0.2
  if (age <= 3) return 0.3
  if (age <= 4) return 0.4
  if (age <= 5) return 0.5
  return 0.6
}

function computeIDV(s: CalcState) {
  const onRoad = ONROAD_BY_MODEL[s.model]
  if (!onRoad) return { min: 40000, max: 400000, def: 100000 }
  const adj = onRoad * (VARIANT_MULT[s.variant] ?? 1)
  const age = Math.max(0, 2026 - parseInt(s.year || '2024', 10))
  const idv = adj * (1 - depreciationForAge(age))
  const round = (n: number) => Math.round(n / 1000) * 1000
  return { min: round(idv * 0.85), max: round(idv * 1.15), def: round(idv) }
}

function computePremium(args: { idv: number; plan: string; addons: string[]; ncbKey: string }) {
  const base = args.idv * 0.03 * (PLAN_MULT[args.plan] ?? 1)
  const addonSum = args.addons.reduce((sum, a) => sum + (ADDON_COST[a] ?? 0), 0)
  return Math.round(base * (NCB_MULT[args.ncbKey] ?? 1) + addonSum)
}

const fmtINR = (n: number) => n.toLocaleString('en-IN')
const toLakhs = (n: number) => Math.round((n / 100000) * 10) / 10

/* ─── Gauge Component (from nextjs-handoff/components/Gauge.tsx) ─────────── */

const CalcGauge = ({
  min,
  max,
  value,
  onChange,
}: {
  min: number
  max: number
  value: number
  onChange: (v: number) => void
}) => {
  const W = 360
  const H = 200
  const cx = W / 2
  const cy = H - 14
  const r = 146
  const strokeW = 18
  const ratio = Math.max(0, Math.min(1, (value - min) / (max - min || 1)))
  const angle = Math.PI - ratio * Math.PI

  const start = { x: cx - r, y: cy }
  const end = { x: cx + r, y: cy }
  const knob = { x: cx + r * Math.cos(angle), y: cy - r * Math.sin(angle) }

  const progressPath = `M ${start.x} ${start.y} A ${r} ${r} 0 0 1 ${knob.x} ${knob.y}`
  const trackPath = `M ${start.x} ${start.y} A ${r} ${r} 0 0 1 ${end.x} ${end.y}`

  const svgRef = useRef<SVGSVGElement>(null)
  const dragging = useRef(false)

  const update = useCallback(
    (cx2: number, cy2: number) => {
      const svg = svgRef.current
      if (!svg) return
      const rect = svg.getBoundingClientRect()
      const sx = (cx2 - rect.left) * (W / rect.width)
      const sy = (cy2 - rect.top) * (H / rect.height)
      const dx = sx - cx
      const dy = cy - sy
      let theta = Math.atan2(dy, dx)
      if (theta < 0) theta = dy < 0 ? 0 : Math.PI
      theta = Math.max(0, Math.min(Math.PI, theta))
      const nr = 1 - theta / Math.PI
      const raw = min + nr * (max - min)
      const stepped = Math.round(raw / 1000) * 1000
      onChange(Math.max(min, Math.min(max, stepped)))
    },
    [min, max, onChange]
  )

  useEffect(() => {
    const move = (e: MouseEvent | TouchEvent) => {
      if (!dragging.current) return
      const p = 'touches' in e ? e.touches[0] : e
      update(p.clientX, p.clientY)
      e.preventDefault()
    }
    const up = () => {
      dragging.current = false
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
    window.addEventListener('touchmove', move, { passive: false })
    window.addEventListener('touchend', up)
    return () => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
      window.removeEventListener('touchmove', move)
      window.removeEventListener('touchend', up)
    }
  }, [update])

  const onDown = (e: React.MouseEvent | React.TouchEvent) => {
    dragging.current = true
    const p = 'touches' in e ? e.touches[0] : e
    update(p.clientX, p.clientY)
  }

  return (
    <div className="relative w-full max-w-[360px] mx-auto">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="block w-full h-auto cursor-pointer touch-none"
        onMouseDown={onDown}
        onTouchStart={onDown}>
        <path
          d={trackPath}
          stroke="var(--color-primary-ring)"
          strokeWidth={strokeW}
          strokeLinecap="round"
          fill="none"
        />
        <path d={progressPath} stroke="var(--color-primary)" strokeWidth={strokeW} strokeLinecap="round" fill="none" />
        <circle
          cx={knob.x}
          cy={knob.y}
          r="16"
          fill="var(--color-primary)"
          stroke="var(--color-on-primary)"
          strokeWidth="4"
        />
      </svg>
      <div className="absolute inset-x-0 bottom-0 text-center pointer-events-none">
        <div className="text-[var(--font-body-sm-size)] leading-[var(--font-body-sm-line)] font-medium text-[var(--color-text-secondary)]">
          IDV
        </div>
        <div className="text-[36px] font-bold tracking-tight text-[var(--color-text-primary)]">
          ₹{toLakhs(value)} Lakhs
        </div>
      </div>
      <div className="absolute left-[6px] text-[13px] font-medium text-[var(--color-text-secondary)]">
        ₹{toLakhs(min)} Lakhs
      </div>
      <div className="absolute right-[6px] text-[13px] font-medium text-[var(--color-text-secondary)]">
        ₹{toLakhs(max)} Lakhs
      </div>
    </div>
  )
}

/* ─── Mobile Detection Hook ────────────────────────────────────────────────── */

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)')
    setIsMobile(mql.matches)
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  return isMobile
}

/* ─── Portal Bottom Sheet ──────────────────────────────────────────────────── */

const MobileBottomSheet = ({
  isOpen,
  onClose,
  title,
  options,
  value,
  onChange,
}: {
  isOpen: boolean
  onClose: () => void
  title: string
  options: string[]
  value: string
  onChange: (option: string) => void
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [shouldRender, setShouldRender] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true)
      document.body.style.overflow = 'hidden'
      const t = setTimeout(() => setIsVisible(true), 10)
      return () => clearTimeout(t)
    }
    setIsVisible(false)
    document.body.style.overflow = 'unset'
    const t = setTimeout(() => setShouldRender(false), 300)
    return () => clearTimeout(t)
  }, [isOpen])

  useEffect(
    () => () => {
      document.body.style.overflow = 'unset'
    },
    []
  )

  if (!mounted || !shouldRender) return null

  return createPortal(
    <>
      <div
        className={`fixed inset-0 bg-black z-[1000] transition-opacity duration-250 ease-out ${
          isVisible ? 'opacity-50' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed bottom-0 left-0 right-0 bg-white z-[1001] max-h-[80vh] flex flex-col transform transition-all duration-300 rounded-t-[24px] ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
        style={{
          transitionTimingFunction: isVisible ? 'cubic-bezier(0.16, 1, 0.3, 1)' : 'cubic-bezier(0.7, 0, 0.84, 0)',
          boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.15)',
        }}>
        <div className="flex justify-center pt-12 pb-8 cursor-pointer" onClick={onClose}>
          <div className="w-32 h-4 bg-[var(--color-border-strong)] rounded-full" />
        </div>
        <div className="flex items-center justify-between px-20 pb-20 border-b" style={{ borderColor: '#EBEBEB' }}>
          <Typography scale="xl" emphasis="bold" as="h3" color="primary">
            {title}
          </Typography>
          <button
            type="button"
            onClick={onClose}
            className="w-32 h-32 flex items-center justify-center rounded-full hover:bg-[var(--color-surface-hover)] transition">
            <Close className="w-20 h-20 text-[var(--color-text-secondary)]" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {options.map((option, index) => (
            <div key={option}>
              <button
                type="button"
                onClick={() => {
                  onChange(option)
                  setTimeout(onClose, 150)
                }}
                className="w-full flex items-center justify-between px-20 py-16 text-left hover:bg-[var(--color-surface-hover)] transition-colors duration-200">
                <Typography scale="base" color="primary">
                  {option}
                </Typography>
                <div
                  className={`w-20 h-20 rounded-full border-2 flex items-center justify-center ${
                    value === option
                      ? 'border-[var(--color-primary)] bg-[var(--color-primary)]'
                      : 'border-[var(--color-border-strong)]'
                  }`}>
                  {value === option && <div className="w-8 h-8 bg-white rounded-full" />}
                </div>
              </button>
              {index < options.length - 1 && <div className="border-b mx-20" style={{ borderColor: '#EBEBEB' }} />}
            </div>
          ))}
        </div>
      </div>
    </>,
    document.body
  )
}

/* ─── Calculator Dropdown (ACKO Dropdown on desktop, custom sheet on mobile) ─ */

const CalcDropdown = ({
  label,
  value,
  options,
  onChange,
  disabled,
  onDisabledClick,
}: {
  label: string
  value: string
  options: string[]
  onChange: (v: string) => void
  disabled?: boolean
  onDisabledClick?: () => void
}) => {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)

  if (isMobile) {
    const handleClick = () => {
      if (disabled) {
        onDisabledClick?.()
        return
      }
      setOpen(true)
    }
    return (
      <div className="acko-dropdown">
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-disabled={disabled}
          onClick={handleClick}
          className={`acko-dropdown-trigger acko-dropdown-trigger-md ${value ? 'acko-dropdown-trigger-filled' : ''} ${
            disabled ? 'opacity-60 cursor-not-allowed' : ''
          }`}>
          <span className={`acko-dropdown-value ${value ? '' : 'acko-dropdown-placeholder'}`}>{value || label}</span>
          <span className={`acko-dropdown-chevron ${open ? 'acko-dropdown-chevron-open' : ''}`} aria-hidden="true">
            <ChevronDown className="w-18 h-18" />
          </span>
        </button>
        <MobileBottomSheet
          isOpen={open}
          onClose={() => setOpen(false)}
          title={`Select ${label}`}
          options={options}
          value={value}
          onChange={onChange}
        />
      </div>
    )
  }

  return (
    <div className="relative [&_.acko-dropdown-label]:hidden">
      <Dropdown
        label=""
        placeholder={label}
        value={value}
        onChange={v => onChange(v as string)}
        options={options.map(option => ({ label: option, value: option }))}
        disabled={disabled}
      />
      {disabled && onDisabledClick && (
        <button
          type="button"
          aria-label={`${label} disabled — select make first`}
          onClick={onDisabledClick}
          className="absolute inset-0 cursor-not-allowed bg-transparent"
        />
      )}
    </div>
  )
}

/* ─── Pill Button ──────────────────────────────────────────────────────────── */

const CalcPill = ({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-16 py-8 text-sm leading-[20px] font-medium rounded-full transition-all duration-200 border ${
      active
        ? 'bg-[var(--color-primary)] text-[var(--color-on-primary)] border-[var(--color-primary)]'
        : 'bg-[var(--color-surface-fill-default)] text-[var(--color-text-primary)] border-[var(--color-border)] hover:border-[var(--color-border-strong)]'
    }`}>
    {children}
  </button>
)

/* ─── Plan Tabs ────────────────────────────────────────────────────────────── */

const PlanTabs = ({ value, onChange, dim }: { value: string; onChange: (v: string) => void; dim?: boolean }) => (
  <div className={`flex gap-8 overflow-x-auto ${styles.scrollbarHide} -mx-2 px-2`}>
    {PLAN_OPTIONS.map(p => {
      const active = value === p
      return (
        <button
          key={p}
          type="button"
          disabled={dim}
          onClick={() => !dim && onChange(p)}
          className={`px-14 py-[11px] text-[13.5px] font-semibold bg-[#fbfbfb] rounded-xl transition whitespace-nowrap shrink-0 ${
            active ? 'border-[1.5px] border-[var(--color-primary)]' : 'border border-[var(--color-border-subtle)]'
          } ${dim ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'}`}>
          {p}
        </button>
      )
    })}
  </div>
)

/* ─── Calculator Main Component ────────────────────────────────────────────── */

const EMPTY: CalcState = { make: '', model: '', variant: '', year: '', addons: [], ncb: '' }

export const CalculatorSectionClient = () => {
  const [state, setState] = useState<CalcState>(EMPTY)
  const [plan, setPlan] = useState('Comprehensive')
  const [error, setError] = useState('')

  const idvRange = useMemo(() => computeIDV(state), [state.model, state.variant, state.year])
  const [idv, setIdv] = useState(idvRange.def)

  const lastKey = useRef('')
  useEffect(() => {
    const key = `${state.model}|${state.variant}|${state.year}`
    if (lastKey.current !== key) {
      setIdv(idvRange.def)
      lastKey.current = key
    }
  }, [state.model, state.variant, state.year, idvRange.def])

  const setField = (patch: Partial<CalcState>) => setState(s => ({ ...s, ...patch }))

  const toggleAddon = (a: string) =>
    setField({ addons: state.addons.includes(a) ? state.addons.filter(x => x !== a) : [...state.addons, a] })

  const status = getStatus(state)
  const onRoad = Math.round((ONROAD_BY_MODEL[state.model] ?? 0) * (VARIANT_MULT[state.variant] ?? 1))
  const premium = computePremium({ idv, plan, addons: state.addons, ncbKey: state.ncb })

  return (
    <div id="calculator" className={`${styles.docSectionBlockGap} ${styles.sectionScrollTarget}`}>
      <Typography scale="4xl" emphasis="bold" as="h2" color="primary" className="text-balance">
        Calculate your bike insurance premium
      </Typography>

      <div className="grid grid-cols-1 md:grid-cols-[1.15fr_1fr] gap-20 items-stretch">
        {/* LEFT — Form Card */}
        <Card variant="primary" className="p-16 md:p-20 lg:p-24">
          <div className="flex flex-col gap-16">
            <Typography scale="xl" emphasis="medium" as="h3" color="primary">
              Select make and model
            </Typography>

            {error && (
              <div className="px-12 py-8 bg-red-50 border border-red-200 rounded-lg">
                <Typography scale="base" className="text-red-600">
                  {error}
                </Typography>
              </div>
            )}

            <div className="grid grid-cols-2 gap-14">
              <CalcDropdown
                label="Make"
                value={state.make}
                options={[...MAKES]}
                onChange={v => {
                  setField({ make: v, model: '', variant: '', year: '' })
                  setError('')
                }}
              />
              <CalcDropdown
                label="Model"
                value={state.model}
                options={state.make ? MODELS_BY_MAKE[state.make] : []}
                onChange={v => setField({ model: v })}
                disabled={!state.make}
                onDisabledClick={() => setError('Please select make first')}
              />
              <CalcDropdown
                label="Variant"
                value={state.variant}
                options={[...VARIANTS]}
                onChange={v => setField({ variant: v })}
                disabled={!state.make}
                onDisabledClick={() => setError('Please select make first')}
              />
              <CalcDropdown
                label="Reg. year"
                value={state.year}
                options={[...YEARS]}
                onChange={v => setField({ year: v })}
                disabled={!state.make}
                onDisabledClick={() => setError('Please select make first')}
              />
            </div>
          </div>
          {/* end Select make and model flex wrapper */}

          <div className="flex flex-col gap-16 mt-28">
            <Typography scale="xl" emphasis="medium" as="h3" color="primary">
              Add-ons
            </Typography>
            <div className="flex flex-wrap gap-8">
              {ADDONS.map(a => (
                <CalcPill key={a} active={state.addons.includes(a)} onClick={() => toggleAddon(a)}>
                  {a}
                </CalcPill>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-16 mt-28">
            <Typography scale="xl" emphasis="medium" as="h3" color="primary">
              No claim bonus
            </Typography>
            <div className="flex gap-8">
              {NCB_OPTIONS.map(n => (
                <CalcPill key={n} active={state.ncb === n} onClick={() => setField({ ncb: n })}>
                  <span className="inline-block min-w-[28px] text-center">{n}</span>
                </CalcPill>
              ))}
            </div>
          </div>
        </Card>

        {/* RIGHT — Widget */}
        <Card variant="primary" className="p-24 min-h-[560px] flex flex-col">
          {status === 'empty' && (
            <>
              <div className="flex-1 flex flex-col justify-center items-center text-center px-20 pt-10">
                <img
                  src="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/calculator_premium_empty_3a7ebd3528.svg"
                  alt=""
                  width={120}
                  height={120}
                  className="w-[120px] h-[120px] object-contain"
                  loading="lazy"
                />
                <div className="text-[22px] font-bold mt-10 tracking-tight">See your price before you commit</div>
                <div className="text-[13.5px] mt-8 max-w-[300px] leading-[1.55] text-[var(--color-text-secondary)]">
                  Tell us about your bike on the left — we&apos;ll show your IDV and an instant premium estimate, no
                  signup needed.
                </div>
              </div>
              <button
                type="button"
                disabled
                className="w-full py-[15px] mt-16 text-[15px] font-medium bg-white/60 border border-[var(--color-border-subtle)] rounded-full cursor-not-allowed text-[var(--color-disabled-text)]">
                Select your bike to continue
              </button>
            </>
          )}

          {status === 'partial' && (
            <>
              <div className="text-center mt-16 text-[12.5px] text-[var(--color-text-secondary)]">
                On-road price <span className="text-[var(--color-text-primary)] font-semibold">₹{fmtINR(onRoad)}</span>{' '}
                · Reg. <span className="text-[var(--color-text-primary)] font-semibold">{state.year}</span>
              </div>
              <div className="mt-14" style={{ opacity: 0.95 }}>
                <CalcGauge min={idvRange.min} max={idvRange.max} value={idv} onChange={setIdv} />
              </div>

              <Card variant="primary" className="mt-32 px-20 py-[18px] text-center">
                <div className="inline-flex items-center gap-8 text-[13px] font-semibold text-[var(--color-on-primary)] px-[11px] py-[5px] bg-[var(--color-primary)] rounded-full mb-10">
                  <span className="w-[6px] h-[6px] bg-[var(--color-on-primary)] rounded-full" />
                  One more step
                </div>
                <div className="text-[15px] font-semibold text-[var(--color-text-primary)]">
                  Select a no claim bonus
                </div>
                <div className="text-[13px] mt-4 leading-[1.5] text-[var(--color-text-secondary)]">
                  Your final premium depends on your claims history — pick one on the left.
                </div>
              </Card>

              <div className="flex-1" />
              <button
                type="button"
                disabled
                className="w-full py-[15px] mt-16 text-[15px] font-medium bg-white/60 border border-[var(--color-border-subtle)] rounded-full cursor-not-allowed text-[var(--color-disabled-text)]">
                View final quote
              </button>
            </>
          )}

          {status === 'complete' && (
            <>
              <PlanTabs value={plan} onChange={setPlan} />
              <div className="text-center mt-16 text-[12.5px] text-[var(--color-text-secondary)]">
                On-road price <span className="text-[var(--color-text-primary)] font-semibold">₹{fmtINR(onRoad)}</span>{' '}
                · Reg. <span className="text-[var(--color-text-primary)] font-semibold">{state.year}</span>
              </div>
              <div className="mt-14">
                <CalcGauge min={idvRange.min} max={idvRange.max} value={idv} onChange={setIdv} />
              </div>

              <Card variant="primary" className="mt-32 px-20 pt-20 pb-[22px] text-center">
                <div className="text-[13px] font-medium text-[var(--color-text-secondary)]">Approximate premium*</div>
                <div className="text-[40px] font-bold tracking-tight leading-[1.1] my-[6px]">₹{fmtINR(premium)}</div>
                <div className="text-[12.5px] leading-[1.4] text-[var(--color-text-secondary)]">
                  Final premium may vary based on coverage choices
                </div>
              </Card>

              <div className="flex-1" />
              <button
                type="button"
                className="w-full py-16 mt-16 text-[15px] font-semibold text-[var(--color-on-primary)] bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] rounded-full transition">
                View final quote
              </button>
            </>
          )}
        </Card>
      </div>
    </div>
  )
}

export const ImpactPremiumSectionClient = ({ onItemExpand }: { onItemExpand?: (title: string) => void } = {}) => {
  const items = [
    {
      id: 'engine',
      label: "Bike's engine capacity",
      desc: 'Higher engine cc means a higher bike insurance premium due to increased power and risk.',
    },
    {
      id: 'idv',
      label: 'Insured declared value',
      desc: 'The current market value of your bike affects your premium - higher IDV means higher premium.',
    },
    {
      id: 'claims',
      label: 'Claims history',
      desc: 'Previous claims history impacts your premium rates and no-claim bonus benefits.',
    },
    {
      id: 'ncb',
      label: 'No Claim Bonus (NCB)',
      desc: 'Each claim-free year earns you a higher NCB discount (up to 50%), significantly reducing your renewal premium.',
    },
    {
      id: 'coverage',
      label: 'Insurance coverage',
      desc: 'Comprehensive coverage costs more than third-party only but provides better protection.',
    },
    {
      id: 'addons',
      label: 'Cost of add-ons',
      desc: 'Additional covers like zero depreciation, engine protection increase your total premium.',
    },
    {
      id: 'age',
      label: 'Age of the bike',
      desc: 'Older bikes typically have lower premiums due to depreciation but may have coverage limitations.',
    },
  ]

  return (
    <div id="premium-factors" className={`${styles.docSectionBlockGap} ${styles.sectionScrollTarget}`}>
      <div className="flex flex-col gap-12">
        <Typography scale="4xl" emphasis="bold" as="h2" color="primary">
          What impacts your bike insurance premium?
        </Typography>
      </div>
      <Card variant="secondary" className="w-full px-24 py-16">
        {/* size="lg" is valid in @acko/css (larger trigger); the package type is stale, hence the cast */}
        <Accordion
          type="single"
          variant="lined"
          size={'lg' as unknown as 'default'}
          defaultValue="engine"
          onChange={openValues => {
            const openedItem = items.find(item => item.id === openValues[0])
            if (openedItem) onItemExpand?.(openedItem.label)
          }}
          items={items.map(item => ({ value: item.id, trigger: item.label, content: item.desc }))}
        />
      </Card>
    </div>
  )
}

const faqs = [
  {
    id: 'faq-1',
    q: 'What is bike insurance?',
    a: 'Bike insurance is a contract between you and an insurance company where the insurer agrees to provide financial protection against losses arising from accidents, theft, or damage to your bike, in exchange for a premium. It is mandatory in India under the Motor Vehicles Act, 1988 — at minimum, third-party liability coverage is required. You can choose from Comprehensive, Own Damage, or Third-Party Only plans depending on your needs.',
  },
  {
    id: 'faq-2',
    q: 'Is bike insurance mandatory in India?',
    a: 'Yes, third-party bike insurance is mandatory under the Motor Vehicles Act, 1988. Riding without at least a third-party policy is a punishable offence with fines up to ₹2,000 and/or imprisonment for up to 3 months. Comprehensive coverage is optional but strongly recommended as it protects your own bike from accidents, theft, fire, and natural disasters in addition to third-party liability.',
  },
  {
    id: 'faq-3',
    q: 'What is covered in comprehensive bike insurance?',
    a: 'Comprehensive bike insurance covers accidental damage to your bike, theft, fire, natural calamities (floods, earthquakes, cyclones), third-party liability (unlimited for bodily injury/death, up to ₹7.5 lakhs for property damage), animal/rodent damage, and transit damage. You can also add optional covers like zero depreciation, engine protection, personal accident, consumables cover, and roadside assistance for enhanced protection.',
  },
  {
    id: 'faq-4',
    q: 'How do I file a bike insurance claim at ACKO?',
    a: 'You can file a claim through the ACKO app or website in 3 simple steps: (1) Go to your policy and tap the "Claim" button to initiate, (2) Upload photos of the damage, describe the incident, and submit documents like your RC and DL, (3) Drop your bike at any authorised service centre for cashless repair. ACKO settles most bike claims within 1-7 days.',
  },
  {
    id: 'faq-5',
    q: 'What is IDV in bike insurance?',
    a: 'IDV (Insured Declared Value) is the current market value of your bike after accounting for depreciation based on its age. It represents the maximum amount your insurer will pay in case of total loss or theft. A higher IDV means better coverage but a slightly higher premium. ACKO offers flexible IDV options — you can choose a higher IDV for better protection or a lower IDV to reduce your premium.',
  },
  {
    id: 'faq-6',
    q: 'What is zero depreciation cover?',
    a: 'Zero depreciation (zero dep) cover ensures you receive the full cost of replaced parts during a claim without any deduction for wear and tear. Without it, you may pay 30-50% of parts cost out of pocket due to depreciation on plastic, rubber, and metal components. It is especially valuable for bikes under 5 years old where part replacement costs are high. At ACKO, zero dep starts from just ₹36/year.',
  },
]

export const FAQSectionClient = () => (
  <div id="faq" className={`${styles.docSectionBlockGap} ${styles.sectionScrollTarget}`}>
    <Typography scale="4xl" emphasis="bold" as="h2" color="primary">
      Common questions about bike insurance
    </Typography>
    <Card variant="secondary" className="w-full px-24 py-16">
      {/* size="lg" is valid in @acko/css (larger trigger); the package type is stale, hence the cast */}
      <Accordion
        type="single"
        variant="lined"
        size={'lg' as unknown as 'default'}
        items={faqs.map(faq => ({ value: faq.id, trigger: faq.q, content: faq.a }))}
      />
    </Card>
  </div>
)
